import React, { useState, useRef, useEffect } from 'react';
import { Checkbox, Icon, Text } from '@perch-labs/shrike';
import {
  Wrapper,
  LabelElement,
  DropdownWrapper,
  TriggerButton,
  Dropdown,
  OptionRow,
} from './multi-select.styles';

type Option = {
  label: string;
  value: string;
};

type Props = {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  name?: string;
};

export const MultiSelect = ({
  options,
  value,
  onChange,
  label = 'Please select an option',
  name = 'multiSelect',
}: Props) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleChange = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const selectedLabels =
    options
      .filter(option => value.includes(option.value))
      .map(option => option.label)
      .join(', ') || 'Please select an option';

  return (
    <Wrapper>
      <LabelElement htmlFor={name}>{label}</LabelElement>
      <input
        type="hidden"
        name={name}
        value={value.join(',')}
        data-testid={`${name}-hidden-input`}
      />
      <DropdownWrapper ref={dropdownRef}>
        <TriggerButton
          id={name}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
        >
          <Text color={value.length ? 'primary' : 'secondary'}>
            {selectedLabels}
          </Text>
          <span>
            {open ? (
              <Icon icon="ChevronUp" margin={{ bottom: '2px', right: '8px' }} />
            ) : (
              <Icon
                icon="ChevronDown"
                margin={{ bottom: '2px', right: '8px' }}
              />
            )}
          </span>
        </TriggerButton>
        {open && (
          <Dropdown role="listbox" aria-multiselectable="true">
            {options.map(option => (
              <OptionRow key={option.value} htmlFor={`${name}-${option.value}`}>
                <Checkbox
                  checked={value.includes(option.value)}
                  onChange={() => handleChange(option.value)}
                  name={name}
                  id={`${name}-${option.value}`}
                />
                <Text>{option.label}</Text>
              </OptionRow>
            ))}
          </Dropdown>
        )}
      </DropdownWrapper>
    </Wrapper>
  );
};
